import { f as CSS_PREFIX, J as JS_PREFIX, D as DECATHLON_NAMESPACE, L as ADDING_TO_CART, M as IS_UPDATED_CLASSNAME, Q as IS_UPDATING_CLASSNAME, R as ADDED_TO_CART, l as PRODUCT_TILE_ADD_TO_CART_START_EVENT, m as PRODUCT_TILE_ADD_TO_CART_COMPLETE_EVENT, P as PRODUCT_TILE_ADD_TO_CART_SUCCESS_EVENT, o as PRODUCT_TILE_ADD_TO_CART_FAIL_EVENT } from './built-constants-439d5572.js';
import { c as cartEventEmitter } from './built-cart-event-emitter-def8079a.js';
import './built-config-a65ae98c.js';
import { b as formatPriceRange, f as formatPriceSingle, g as getValidPriceList } from './built-price-format-dc3d77ee.js';
import { o as onAddToCart } from './built-on-add-to-cart-59888dcc.js';
import { s as showElement, h as hideElement } from './built-element-utils-09877efc.js';
import { c as createState } from './built-create-state-8c555f12.js';

// @ts-check
const PRODUCT_TILE_ADD_TO_CART_PREFIX = JS_PREFIX + "ProductTileAddToCart-";
const PRODUCT_TILE_SELECTOR = "." + CSS_PREFIX + "ProductTile";
const PRODUCT_TILE_ADD_TO_CART_FORM_SELECTOR = "." + PRODUCT_TILE_ADD_TO_CART_PREFIX + "Form";
const PRODUCT_TILE_MESSAGE_SELECTOR = "." + PRODUCT_TILE_ADD_TO_CART_PREFIX + "Message";
const PRODUCT_TILE_MESSAGE_ID = 'productTileMessageId';

// @ts-check
/**
 * Will reference the parent Product Tile root element
 */

let productTileEl = null;
const COLOR_SWATCH_SELECTOR = "." + JS_PREFIX + "ColorSwatchList-action";
/**
 * Sets up listeners on all color swatch elements
 * @param {(color: string) => void} onColorSelectCallback Called with the selected color when a color is hovered
 */

const onColorSelect = onColorSelectCallback => {
  productTileEl.querySelectorAll(COLOR_SWATCH_SELECTOR).forEach(colorSwatchEl => {
    colorSwatchEl.addEventListener('mouseenter', event => {
      // Call the callback with the new color swatch data
      onColorSelectCallback(
      /** @type {HTMLElement} */
      event.target.dataset.color);
    });
  });
};
/**
 * Initialize setup
 *
 * @param {HTMLElement} el
 */

const init = el => {
  productTileEl = el;
};

// @ts-check
/**
 * @typedef {Object} PriceData
 * @property {string} [prices] The variant-level price values
 * @property {boolean} [priceVaries] Does the price value vary?
 * @property {string} [compareAtPrices] The variant-level compare_at_price values
 * @property {boolean} [compareAtPriceVaries] Does the compare_atPrice value vary?
 * @property {string} [pricesDelimiter] The prices list delimiter
 * @property {string} [color] The currently chosen color
 * @property {boolean} [isLastCall] Should the "Last Call" flag display?
 */

/**
 * @typedef {Object} FormatPriceData
 * @property {string} pricesDelimiter The prices list delimiter
 * @property {string} [prices] The variant-level price values
 * @property {boolean} [priceVaries] Does the price vary at the variant-level?
 */

/**
 * Formats the price for display
 *
 * @param {FormatPriceData} state
 * @returns {string}
 */

const formatPrice = ({
  prices,
  pricesDelimiter,
  priceVaries
}) => {
  if (priceVaries) {
    return formatPriceRange(prices, pricesDelimiter);
  }

  if (!prices) {
    return '';
  } // If prices don't vary, all prices are the same
  // so go ahead and use the first one from the _valid_ list.


  return formatPriceSingle(getValidPriceList(prices, pricesDelimiter)[0]);
};
/**
 * Initializes scoped to a single Product Tile
 *
 * @param {HTMLElement} productTileEl
 */


const init$1 = productTileEl => {
  /** @type {NodeListOf<HTMLElement>} */
  const currentPriceEls = productTileEl.querySelectorAll("." + JS_PREFIX + "ProductTile-currentPrice");
  /** @type {NodeListOf<HTMLElement>} */

  const crossedOutPriceEls = productTileEl.querySelectorAll("." + JS_PREFIX + "ProductTile-crossedOutPrice");
  /**
   * Updates the Price UI
   *
   * @param {PriceData} priceData
   */

  const update = ({
    pricesDelimiter,
    prices,
    priceVaries,
    compareAtPrices,
    compareAtPriceVaries,
    color
  }) => {
    // Handle "default" reset of data, this occurs when no color is selected.
    if (!color) {
      currentPriceEls.forEach(currentPriceEl => {
        currentPriceEl.textContent = prices;
      });
      crossedOutPriceEls.forEach(crossedOutPriceEl => {
        const crossedOutPrice = priceVaries ? '' : compareAtPrices;
        showElement(crossedOutPriceEl);
        crossedOutPriceEl.textContent = crossedOutPrice;
      });
      return;
    }

    if (!pricesDelimiter || !prices) return;
    /** @type {FormatPriceData} */

    const sharedPriceFormatData = {
      pricesDelimiter,
      priceVaries
    };
    /**
     * If either `variant.price` or `variant.compare_at_price` varies,
     * then we need to show a full range of all the prices combined
     * @see https://app.gitbook.com/@decathlonusa/s/shopify/product-feature/product-tiles-and-carousel#range-of-prices
     */

    let currentPrice = '';
    let crossedOutPrice = '';
    const doPricesOrCompareAtPricesVary = priceVaries || compareAtPriceVaries;

    if (doPricesOrCompareAtPricesVary) {
      currentPrice = formatPrice(Object.assign({}, sharedPriceFormatData, {
        // In this case, we want to render a range consisting of _all_ prices
        prices: "" + prices + pricesDelimiter + compareAtPrices,
        priceVaries: doPricesOrCompareAtPricesVary
      })); // In this case, we don't need to render a crossed out price

      crossedOutPrice = '';
    } else {
      // In this case, prices don't vary so pass them directly along
      currentPrice = formatPrice(Object.assign({}, sharedPriceFormatData, {
        prices
      })); // In this case, we do render a crossed out price, format accordingly

      crossedOutPrice = formatPrice(Object.assign({}, sharedPriceFormatData, {
        prices: compareAtPrices
      }));
    }

    currentPriceEls.forEach(currentPriceEl => {
      currentPriceEl.textContent = currentPrice;
    });
    crossedOutPriceEls.forEach(crossedOutPriceEl => {
      showElement(crossedOutPriceEl);
      crossedOutPriceEl.textContent = crossedOutPrice;
    });
  };

  return {
    update
  };
};

// @ts-check
/**
 * @typedef {Object} Image
 * @property {string} [url]
 * @property {string} [alt]
 */

/**
 * @typedef {Object} FeatureImageState
 * @property {Image} image1
 * @property {Image} image2
 * @property {boolean} isHovered
 */

/**
 * @param {HTMLElement} productTileEl
 */

const init$2 = productTileEl => {
  /** @type {HTMLImageElement} */
  const imageEl = productTileEl.querySelector("." + JS_PREFIX + "ProductTile-featureImage");
  /** @type {FeatureImageState} */

  const initialState = {
    image1: {},
    image2: {},
    isHovered: false
  };
  const state = createState(initialState);

  if (imageEl) {
    state.onChange(({
      image1,
      image2,
      isHovered
    }) => {
      const {
        url,
        alt
      } = isHovered && imageEl.hasAttribute('data-show-secondary-image') ? image2 : image1; // If the image already has a `src` update the `src` directly, otherwise update the `data-src`
      // That way we don't unnecessarily force-load a lazy-loaded image

      if (imageEl.src) imageEl.src = url;else imageEl.dataset.src = url;
      imageEl.alt = alt;
    });
    imageEl.addEventListener('mouseenter', () => state.updateState({
      isHovered: true
    }));
    imageEl.addEventListener('mouseleave', () => state.updateState({
      isHovered: false
    }));
  }

  return {
    /**
     * Updates the feature image to the selected color
     * @param {{ image1: Image, image2: Image }} newImages
     */
    updateImages: newImages => state.updateState(newImages)
  };
};

// @ts-check
/**
 * @typedef {Object} FreeShippingData
 * @property {boolean} [isFreeShipping] "Free Shipping" vs "Free shipping over ${{amount}}"
 * @see https://app.gitbook.com/@decathlonusa/s/shopify/product-feature/product-tiles-and-carousel#delivery-options
 */

/**
 * Initializes scoped to a single Product Tile
 *
 * @param {HTMLElement} productTileEl
 */

const init$3 = productTileEl => {
  /** @type {HTMLElement} */
  const freeShippingOverThresholdEl = productTileEl.querySelector("." + JS_PREFIX + "DeliveryOptions-freeShippingOverThreshold");
  /**
   * Shows/hides the "over ${{amount}}" free shipping element
   *
   * @param {FreeShippingData} freeShippingData
   */

  const updateFreeShipping = ({
    isFreeShipping
  }) => {
    if (!freeShippingOverThresholdEl) return;

    if (isFreeShipping) {
      hideElement(freeShippingOverThresholdEl);
    } else {
      showElement(freeShippingOverThresholdEl);
    }
  };

  return {
    updateFreeShipping
  };
};

// @ts-check
/**
 * @typedef {Object} LastCallData
 * @property {boolean} [isLastCall] Should the "Last Call" flag display?
 */

/**
 * Initializes scoped to a single Product Tile
 *
 * @param {HTMLElement} productTileEl
 */

const init$4 = productTileEl => {
  /** @type {HTMLElement} */
  const lastCallFlagEl = productTileEl.querySelector("." + JS_PREFIX + "ProductFlag-lastCall");
  /**
   * Shows/hides the "Last Call" flag
   *
   * @param {LastCallData | undefined} lastCallData
   */

  const update = ({
    isLastCall
  } = {}) => {
    if (!lastCallFlagEl) return;

    if (isLastCall) {
      showElement(lastCallFlagEl);
    } else {
      hideElement(lastCallFlagEl);
    }
  };

  return {
    update
  };
};

// @ts-check
/**
 * @typedef {Object} AvailabilityInfo
 * @property {number} [availableVariants] Sourced from `data-available-variants` attribute, the number of available variants
 * @property {number} [totalVariants] Sourced from `data-total-variants` attribute, the total number of variants
 */

/** @param {HTMLElement} productTileEl */

const init$5 = productTileEl => {
  const inventoryStatusEl = productTileEl.querySelector("." + JS_PREFIX + "ProductTile-inventoryStatus");
  return {
    /** @param {AvailabilityInfo} info */
    update({
      availableVariants,
      totalVariants
    }) {
      if (!inventoryStatusEl) return;
      const shouldShowlimitedSizes = availableVariants / totalVariants < 0.5 && availableVariants > 0;
      if (shouldShowlimitedSizes) showElement(inventoryStatusEl);else hideElement(inventoryStatusEl);
    }

  };
};

var _window$DECATHLON_NAM, _window$DECATHLON_NAM2;
const TILE_SUCCESS_MESSAGE_TIMEOUT = ((_window$DECATHLON_NAM = window[DECATHLON_NAMESPACE]) == null ? void 0 : _window$DECATHLON_NAM.productTileSuccessMessageTimeout) || 5000;
const TILE_FAILURE_MESSAGE_TIMEOUT = ((_window$DECATHLON_NAM2 = window[DECATHLON_NAMESPACE]) == null ? void 0 : _window$DECATHLON_NAM2.productTileFailureMessageTimeout) || 5000; // The messageEl will be set in each ATC event (set in displayTileMessage)

let messageEl = null; // Holder for message display timeout IDs

const messageTimeouts = {}; // Holder for default ATC button text (set in buttonToggleAtcInProgress)

let DEFAULT_ATC_TEXT = '';
/**
 * Get a message ID from the message element, for managing message timeouts
 * @param {HTMLElement} messageEl
 * @returns {string}
 */

const getMessageId = messageEl => messageEl == null ? void 0 : messageEl.dataset[PRODUCT_TILE_MESSAGE_ID];
/**
 * Toggle the in-progress state of the tile during ATC event (animates progress border)
 * @param {Object} params
 * @param {import('../product-tile-atc').ProductTileAddToCartFormEvent} params.event
 * @param {boolean} [params.isInProgress = false]
 */


const tileToggleAtcInProgress = ({
  event,
  isInProgress = false
}) => {
  var _event$target;

  const productTileEl = event == null ? void 0 : (_event$target = event.target) == null ? void 0 : _event$target.closest(PRODUCT_TILE_SELECTOR);
  if (!productTileEl) return;

  if (isInProgress) {
    productTileEl.classList.remove(IS_UPDATED_CLASSNAME);
    productTileEl.classList.add(IS_UPDATING_CLASSNAME);
  } else {
    productTileEl.classList.remove(IS_UPDATING_CLASSNAME);
    productTileEl.classList.add(IS_UPDATED_CLASSNAME);
  }
};
/**
 * Hides tile message from view, cancels any existing
 * queued message hides.
 * @param {HTMLElement} messageEl
 * @param {string} messageId
 * @returns (() => void) - to use as a timeout callback
 */

const hideTileMessage = (messageEl, messageId) => () => {
  clearTimeout(messageTimeouts[messageId]);
  messageEl.textContent = '';
};
/**
 * Displays cart message on tile, and hides after specified timeout
 *
 * @param {Object} params
 * @param {import('../product-tile-atc').ProductTileAddToCartFormEvent} params.event
 * @param {string} params.message
 * @param {number} params.timeout
 * @TODO - Implement message mapping as is done on product page for buybox and
 *         (in a different way) Preact components in PAC and Cart
 */


const displayTileMessage = ({
  event,
  message,
  timeout
}) => {
  var _event$target2;

  const productTileEl = (_event$target2 = event.target) == null ? void 0 : _event$target2.closest(PRODUCT_TILE_SELECTOR);
  messageEl =
  /** @type {HTMLElement} */
  productTileEl == null ? void 0 : productTileEl.querySelector(PRODUCT_TILE_MESSAGE_SELECTOR);

  if (!messageEl) {
    return;
  }

  messageEl.textContent = message;
  const messageId = getMessageId(messageEl);
  /**
   * If for any reason we can't get a unique ID (unlikely, since it's derived
   * from a liquid `for` loop), at least messages will be set and updated.
   */

  if (!messageId) {
    return;
  } // Clear any existing timeout for this tile's message


  if (messageTimeouts[messageId]) {
    clearTimeout(messageTimeouts[messageId]);
  } // Get a new callback for setTimeout, passing in the message element and its ID


  const hideMessageTimeoutCb = hideTileMessage(messageEl, messageId); // Assign this timeout to the messageTimeouts object for clearing by reference (messageId)

  messageTimeouts[messageId] = setTimeout(hideMessageTimeoutCb, timeout);
};
/**
 * Convenience function for setting a default messsage on successful ATC
 * @param {import('../product-tile-atc').ProductTileAddToCartFormEvent} event
 */

const displayAtcSuccessMessage = event => {
  displayTileMessage({
    event,
    message: ADDED_TO_CART,
    timeout: TILE_SUCCESS_MESSAGE_TIMEOUT
  });
};
/**
 * Displays tile ATC failure message on tile, and hides after specified timeout
 *
 * @param {Object} params
 * @param {import('../product-tile-atc').ProductTileAddToCartFormEvent} params.event
 * @param {string} params.message
 */

const displayAtcFailureMessage = ({
  event,
  message
}) => {
  displayTileMessage({
    event,
    message,
    timeout: TILE_FAILURE_MESSAGE_TIMEOUT
  });
};
/**
 * Toggle in-progress state of button during ATC
 * @param {Object} params
 * @param {import('../product-tile-atc').ProductTileAddToCartFormEvent} params.event
 * @param {boolean} [params.isInProgress = false]
 */

const buttonToggleAtcInProgress = ({
  event,
  isInProgress = false
}) => {
  var _event$target3;

  const submitInputEl = event == null ? void 0 : (_event$target3 = event.target) == null ? void 0 : _event$target3.submitInput;
  if (!submitInputEl) return;
  DEFAULT_ATC_TEXT = DEFAULT_ATC_TEXT || (submitInputEl == null ? void 0 : submitInputEl.value);

  if (isInProgress) {
    submitInputEl.setAttribute('disabled', '');
    submitInputEl.value = ADDING_TO_CART;
  } else {
    submitInputEl.removeAttribute('disabled');
    submitInputEl.value = DEFAULT_ATC_TEXT;
  }
};

// @ts-check
/**
 * Listeners for updating the state of the button itself
 */

cartEventEmitter.on(PRODUCT_TILE_ADD_TO_CART_START_EVENT, event => {
  buttonToggleAtcInProgress({
    event,
    isInProgress: true
  });
});
cartEventEmitter.on(PRODUCT_TILE_ADD_TO_CART_COMPLETE_EVENT, event => {
  buttonToggleAtcInProgress({
    event,
    isInProgress: false
  });
});
/**
 * Initializes Add to cart button to use JS/AJAX
 * @param {HTMLElement} productTileEl
 */

const init$6 = productTileEl => {
  productTileEl.querySelectorAll(PRODUCT_TILE_ADD_TO_CART_FORM_SELECTOR).forEach(addToCartForm => addToCartForm.addEventListener('submit', onAddToCart));
};

// @ts-check
/**
 * Module-specific type definitions
 */

/**
 * @typedef {Object} ProductTileState
 * Defines the state properties for Product Tile
 *
 * @property {HTMLElement} [productTileEl]
 * @property {string} [chosenColor]
 */

/**
 * Module-specific constants
 */

const PRODUCT_TILE_CSS_SELECTOR = "." + JS_PREFIX + "ProductTile"; // This is the color swatch wrapper element selector, it holds the default state values

const PRODUCT_TILE_DEFAULTS_SELECTOR = "." + JS_PREFIX + "ProductTile-defaults";
/**
 * @typedef {Object} SwatchOptions
 * Options that come from data attributes on the swatch elements

 * @property {string} [productId] From the `data-product-id` attribute
 * @property {string} [color] From the `color` attribute
 * @property {string} [image1] Sourced from `data-image` attribute, the main image for the selected color
 * @property {string} [image1Alt] Sourced from `data-image-alt` attribute, the alt text for the main image for the selected color
 * @property {string} [image2] Sourced from `data-image2` attribute, the 2nd image for the selected color
 * @property {string} [image2Alt] Sourced from `data-image2-alt` attribute, the alt text for the 2nd image for the selected color
 * @property {string} [prices] Sourced from `data-prices` attribute, the variant-level price values
 * @property {string} [pricesDelimiter] Sourced from `data-prices-delimiter` attribute, prices list delimiter
 * @property {boolean} [priceVaries] Sourced from Shopify `product.priceVaries`, product-level value
 * @property {string} [compareAtPrices] Sourced from `data-compare-at-prices` attribute, variant-level compare_at_price values
 * @property {boolean} [compareAtPriceVaries] Sourced from Shopify `product.compare_at_price_varies` value
 * @property {boolean} [isFreeShipping] Sourced from `data-is-free-shipping` attribute, whether a product qualifies for the "Free shipping" (price >= $50)
 * @property {number} [availableVariants] Sourced from `data-available-variants` attribute, the number of available variants
 * @property {number} [totalVariants] Sourced from `data-total-variants` attribute, the total number of variants
 */

/**
 * @typedef {Object} ImageData
 *
 * @property {import('./feature-image').Image} image1
 * @property {import('./feature-image').Image} image2
 */
// This value is not tied to the `data-prices-delimiter` attribute and can be anything

const pricesDelimiter = '==';
/**
 * Scoped Product Tile
 *
 * This allows each Product Tile to have its own scoped state.
 *
 * @param {HTMLElement} productTileEl
 */

const initProductTile = productTileEl => {
  /** @type {HTMLElement} */
  const defaultsEl = productTileEl.querySelector(PRODUCT_TILE_DEFAULTS_SELECTOR); // This provides us access to the Product Tile default UI state data

  const defaults = defaultsEl ? defaultsEl.dataset : {};
  /**
   * @type {ProductTileState}
   */

  const productTileState = {
    productTileEl
  };
  /** @type {SwatchOptions[]} */

  const allSwatches =
  /** @type {HTMLElement[]} */
  [...productTileEl.querySelectorAll(COLOR_SWATCH_SELECTOR)].map(({
    dataset
  }) => {
    var _window$DECATHLON_NAM;

    return {
      color: dataset.color,
      productId: dataset.productId,
      prices: dataset.prices,
      pricesDelimiter: dataset.pricesDelimiter,
      image1: dataset.image,
      image1Alt: dataset.imageAlt,
      image2: dataset.image2,
      image2Alt: dataset.image2Alt,
      compareAtPrices: dataset.compareAtPrices,
      priceVaries: dataset.priceVaries === 'true',
      compareAtPriceVaries: dataset.compareAtPriceVaries === 'true',
      isFreeShipping: dataset.isFreeShipping === 'true' || ((_window$DECATHLON_NAM = window[DECATHLON_NAMESPACE]) == null ? void 0 : _window$DECATHLON_NAM.isAllShippingFree) === true,
      availableVariants: Number(dataset.availableVariants),
      totalVariants: Number(dataset.totalVariants),
      isLastCall: dataset.isLastCall === 'true'
    };
  });
  /** Map of all the colors to all the options associated with those colors (retrieved from data attributes) */

  const allSwatchOptions = allSwatches.reduce((allSwatchOptions, swatchOptions) => {
    return Object.assign({}, allSwatchOptions, {
      [swatchOptions.color]: swatchOptions
    });
  },
  /** @type {{[color: string]: SwatchOptions}} */
  {});
  const state = createState(productTileState);
  /**
   * @param {string | undefined} [chosenColor]
   * @returns {ImageData}
   */

  const getImagesFromChosenColor = chosenColor => {
    // Attempt to return color specific data
    const swatchOptions = allSwatchOptions[chosenColor];

    if (swatchOptions) {
      return {
        image1: {
          url: swatchOptions.image1,
          alt: swatchOptions.image1Alt
        },
        image2: {
          url: swatchOptions.image2,
          alt: swatchOptions.image2Alt
        }
      };
    } // Otherwise return Product Tile defaults


    return {
      image1: {
        url: defaults.defaultImage,
        alt: defaults.defaultImageAlt
      },
      image2: {
        url: defaults.defaultImage2,
        alt: defaults.defaultImage2Alt
      }
    };
  };
  /**
   * Computes/retrieves the number of available and total variants for the selected color,
   * or for all colors if no color is selected.
   * This is used for showing/hiding "Limited Sizes" in the UI
   * @param {string | undefined} [chosenColor]
   * @returns {import('./inventory-status').AvailabilityInfo}
   */


  const getAvailabilityInfoFromColor = chosenColor => {
    // Attempt to return color specific data
    const matchingSwatchOptions = allSwatchOptions[chosenColor];
    if (matchingSwatchOptions) return matchingSwatchOptions; // Otherwise return Product Tile defaults

    return {
      availableVariants: Number(defaults.defaultAvailableVariants),
      totalVariants: Number(defaults.defaultTotalVariants)
    };
  };
  /**
   * Gets the price options for the selected color,
   * or the default product state if there is no selected color
   * @param {string | undefined} chosenColor
   * @returns {import('./price').PriceData}
   */


  const getPriceOptionsFromColor = chosenColor => {
    // Attempt to return color specific data
    const matchingSwatchOptions = allSwatchOptions[chosenColor];
    if (matchingSwatchOptions) return matchingSwatchOptions; // Otherwise return Product Tile defaults

    return {
      color: chosenColor,
      pricesDelimiter,
      priceVaries: defaults.defaultPriceVaries === 'true',
      prices: defaults.defaultPrice,
      compareAtPriceVaries: defaults.defaultCompareAtPriceVaries === 'true',
      compareAtPrices: defaults.defaultCompareAtPrice,
      isLastCall: defaults.defaultIsLastCall === 'true'
    };
  };

  const getShippingDataFromColor = chosenColor => {
    var _window$DECATHLON_NAM2;

    // Attempt to return color specific data
    const matchingSwatchOptions = allSwatchOptions[chosenColor];
    if (matchingSwatchOptions) return matchingSwatchOptions;
    return {
      isFreeShipping: defaults.defaultIsFreeShipping === 'true' || ((_window$DECATHLON_NAM2 = window[DECATHLON_NAMESPACE]) == null ? void 0 : _window$DECATHLON_NAM2.isAllShippingFree) === true
    };
  }; // Initialize the child UI components first


  init(productTileEl);
  const featureImageInstance = init$2(productTileEl);
  const freeShippingInstance = init$3(productTileEl);
  const lastCallFlagInstance = init$4(productTileEl);
  const priceInstance = init$1(productTileEl);
  const inventoryStatusInstance = init$5(productTileEl);
  init$6(productTileEl); // Pass the initial images to the feature image component
  // in case the image gets hovered before a swatch gets selected

  featureImageInstance.updateImages(getImagesFromChosenColor());
  inventoryStatusInstance.update(getAvailabilityInfoFromColor());

  state.onChange(({
    chosenColor
  }) => {
    const priceOptions = getPriceOptionsFromColor(chosenColor); // Here, we can update any other UI elements via their appropriate modules

    priceInstance.update(priceOptions);
    featureImageInstance.updateImages(getImagesFromChosenColor(chosenColor));
    freeShippingInstance.updateFreeShipping(getShippingDataFromColor(chosenColor));
    lastCallFlagInstance.update(priceOptions);
    inventoryStatusInstance.update(getAvailabilityInfoFromColor(chosenColor));
  }, state => [state.chosenColor]); // Then setup the callback to be called when a color is selected

  onColorSelect(chosenColor => state.updateState({
    chosenColor
  })); // Reset Product Tile state when hovering off of Product Tile

  productTileEl.addEventListener('mouseleave', () => {
    state.updateState({
      chosenColor: undefined
    });
  });
};

const initEventEmitterListeners = () => {
  /**
   * Listen for EventEmitter events and trigger updates to portions of the
   * tile that aren't managed in other files
   * @TODO - Determine whether there's any way to get typing on payloads
   *         from emit definitions
   */
  cartEventEmitter.on(PRODUCT_TILE_ADD_TO_CART_START_EVENT, event => {
    tileToggleAtcInProgress({
      event,
      isInProgress: true
    });
  });
  cartEventEmitter.on(PRODUCT_TILE_ADD_TO_CART_COMPLETE_EVENT, event => {
    tileToggleAtcInProgress({
      event,
      isInProgress: false
    });
  });
  cartEventEmitter.on(PRODUCT_TILE_ADD_TO_CART_SUCCESS_EVENT, ({
    event
  }) => displayAtcSuccessMessage(event));
  cartEventEmitter.on(PRODUCT_TILE_ADD_TO_CART_FAIL_EVENT, displayAtcFailureMessage);
};
/**
 * Initializes all child UI modules
 */


const initProductTiles = () => {
  document.querySelectorAll(PRODUCT_TILE_CSS_SELECTOR).forEach(initProductTile);
  initEventEmitterListeners();
};

export { initProductTiles as i };
